/* Public Open Play session page — mirrors the public Tournament page:
   facility branding header, meta pills, Register CTA, "Who's In" roster grid
   (avatars, divisions, waitlist badges) + live courts/queue/leaderboard when
   the session is running. */
import { Fragment, useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Zap, Users, AlertTriangle, Pause, Trophy, ArrowLeft, CalendarDays, Clock, Radio,
  Share2, Download,
} from 'lucide-react';
import axios from 'axios';
import { fmt12 } from '../utils/sportsUtils';
import { fmtDate, fmtDateShort } from '../utils/dates';
import MatchupCard, { fmtWait } from '../components/MatchupCard';
import MedalBadge from '../components/MedalBadge';
import { shareChampionsCard, downloadChampionsCard } from '../utils/championsCard';
import OpenPlayAwards, { HowRanked, isFairRule, isWinsRule, fairRecord, unrankedWhy, cardAwards } from '../components/OpenPlayAwards';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND}/api` });
const RED = '#E63946';

const STATUS_PILL = {
  draft: { label: 'Coming Soon', color: '#737373' },
  registration_open: { label: 'Registration Open', color: '#22C55E' },
  registration_closed: { label: 'Registration Closed', color: '#F59E0B' },
  active: { label: 'LIVE', color: RED },
  ended: { label: 'Ended', color: '#3B82F6' },
};

function MetaPill({ children, accent }) {
  return (
    <span className="text-xs px-2.5 py-1 rounded-full font-medium flex items-center gap-1"
      style={accent
        ? { background: '#0D2B1A', color: '#22C55E', border: '1px solid #1A4A2E', fontWeight: 700 }
        : { background: '#1A1A1A', color: '#A3A3A3', border: '1px solid #2A2A2A' }}>
      {children}
    </span>
  );
}

// ── Hero header (facility strip + name + pills + CTA) ─────────────────────
// Walk-up "find my match" (UI checklist 1.3) — phone in, personal pass out.
function FindMyPass({ slug, sid }) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {found, players}
  const search = async () => {
    if (phone.replace(/\D/g, '').length < 7) return;
    setBusy(true);
    try {
      const r = await api.get(`/sports/public/${slug}/open-play/${sid}/find-pass`, { params: { phone } });
      setResult(r.data);
      if (r.data.found && r.data.players.length === 1) {
        window.location.href = `/sports/open-play/player/${r.data.players[0].token}`;
      }
    } catch { setResult({ found: false, players: [] }); }
    setBusy(false);
  };
  if (!open) {
    return (
      <button data-testid="op-find-pass-btn" onClick={() => setOpen(true)}
        className="px-5 py-3 rounded-full text-sm font-black uppercase tracking-wider shrink-0"
        style={{ background: 'linear-gradient(135deg, #FF1A3E, #FF6B35)', color: '#fff', border: 'none', cursor: 'pointer', letterSpacing: '0.08em', boxShadow: '0 0 24px rgba(255,26,62,0.45)', fontFamily: 'Oswald, sans-serif' }}>
        Playing tonight? Find your match
      </button>
    );
  }
  return (
    <div className="w-full sm:w-auto" style={{ minWidth: 260 }}>
      <div className="flex gap-2">
        <input autoFocus type="tel" value={phone} onChange={e => setPhone(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && search()}
          placeholder="Phone you registered with" data-testid="op-find-pass-input"
          className="flex-1 rounded-full px-4 py-2.5 text-sm"
          style={{ background: '#0F0F0F', border: '1px solid rgba(255,26,62,0.5)', color: '#fff', outline: 'none' }} />
        <button onClick={search} disabled={busy} data-testid="op-find-pass-go"
          className="px-4 py-2.5 rounded-full text-xs font-black uppercase"
          style={{ background: 'linear-gradient(135deg, #FF1A3E, #FF6B35)', color: '#fff', border: 'none', cursor: 'pointer', boxShadow: '0 0 18px rgba(255,26,62,0.4)' }}>
          {busy ? '…' : 'Find'}
        </button>
      </div>
      {result && !result.found && (
        <p className="text-xs mt-2" style={{ color: '#F59E0B' }}>No registration under that number — check with the desk.</p>
      )}
      {result?.found && result.players.length > 1 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {result.players.map(p => (
            <a key={p.token} href={`/sports/open-play/player/${p.token}`}
              className="px-3 py-1.5 rounded-full text-xs font-bold"
              style={{ background: 'rgba(255,26,62,0.12)', border: '1px solid rgba(255,26,62,0.45)', color: '#fff', textDecoration: 'none' }}>
              {p.name} →
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function SessionHero({ session, canRegister, onRegister, live, lastUpdated, slug, sid, reconnecting }) {
  const fac = session.facility || {};
  const pill = STATUS_PILL[session.status] || STATUS_PILL.draft;
  const fee = parseFloat(session.entry_fee || 0);
  const isLive = session.status === 'active';
  // Situation line — computed from the live payload (honest counts only)
  const divs = live?.divisions || [];
  // Slots go queued → active → completed; 'playing' is a PLAYER status and the
  // live payload's court list field is `courts` (old names matched broken seed data).
  const playingNow = divs.reduce((n, d) =>
    n + (d.courts || []).reduce((m, cq) => m + (cq.slots || []).filter(s => s.status === 'active').length, 0), 0);
  const queuedMatches = divs.reduce((n, d) => n + (d.pool || []).length + (d.open_games || []).length, 0);
  const calledMatches = divs.reduce((n, d) => n + (d.tba || []).length, 0);
  // The set actually being played (R2-44) — shown only when every division
  // is on the same one; the newest GENERATED set was a different number.
  const playingSets = [...new Set(divs.map(d => d.playing_set_number).filter(n => n != null))];
  const setNo = playingSets.length === 1 ? playingSets[0] : null;
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    if (!isLive) return undefined;
    const iv = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [isLive]);
  const agoSec = lastUpdated ? Math.max(0, Math.round((nowTick - lastUpdated) / 1000)) : null;
  return (
    <div className="rounded-2xl mb-5 overflow-hidden" style={{ background: 'linear-gradient(135deg, #1A0A0C 0%, #0F0F0F 60%, #0A0A12 100%)', border: '1px solid #2A2A2A' }}>
      {fac.logo_url && (
        <div className="flex items-center gap-3 px-5 pt-4 pb-3" style={{ borderBottom: '1px solid #1E1E1E' }}>
          <img src={fac.logo_url} alt={fac.name} style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
          <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#525252', letterSpacing: '0.12em' }}>{fac.name}</span>
        </div>
      )}
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5 mb-1 flex-wrap">
              <Zap className="h-6 w-6 shrink-0" style={{ color: RED }} />
              <h1 className="text-2xl font-black leading-tight" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff', letterSpacing: '0.03em' }}>
                {(session.name || '').toUpperCase()}
              </h1>
              <span data-testid="op-public-status-pill" className="text-[11px] px-2.5 py-1 rounded-full font-bold uppercase tracking-wide inline-flex items-center gap-1.5"
                style={{ background: `${pill.color}18`, color: pill.color, border: `1px solid ${pill.color}40` }}>
                {session.status === 'active' && <span className="h-2 w-2 rounded-full animate-pulse" style={{ background: pill.color }} />}
                {pill.label}
              </span>
            </div>
            {session.description && <p className="text-sm mt-1 mb-2" style={{ color: '#A3A3A3', lineHeight: 1.5 }}>{session.description}</p>}
            <div className="flex flex-wrap gap-2 mt-2">
              <MetaPill>{session.game_mode === 'singles' ? 'Singles' : 'Doubles'} Rotation</MetaPill>
              {session.date && <MetaPill><CalendarDays className="h-3 w-3" /> {fmtDateShort(session.date)}</MetaPill>}
              {session.time_start && <MetaPill><Clock className="h-3 w-3" /> {fmt12(session.time_start)} – {fmt12(session.time_end)}</MetaPill>}
              <MetaPill><Users className="h-3 w-3" /> {session.player_count || 0} player{(session.player_count || 0) !== 1 ? 's' : ''}</MetaPill>
              {fee > 0 && <MetaPill accent>₱{fee.toLocaleString()} entry fee</MetaPill>}
            </div>
            {/* Live situation line — the broadcast status bug (checklist 1.3) */}
            {isLive && live && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3" data-testid="op-hero-situation">
                {setNo != null && <span className="text-xs font-bold" style={{ color: '#fff' }}>Set {setNo}</span>}
                {setNo != null && <span style={{ color: '#3A3A3A' }}>·</span>}
                <span className="text-xs" style={{ color: '#A6A69E' }}>{playingNow} match{playingNow === 1 ? '' : 'es'} on court</span>
                <span style={{ color: '#3A3A3A' }}>·</span>
                {calledMatches > 0 && (
                  <>
                    <span className="text-xs" style={{ color: '#F59E0B' }}>{calledMatches} called — waiting for a court</span>
                    <span style={{ color: '#3A3A3A' }}>·</span>
                  </>
                )}
                <span className="text-xs" style={{ color: '#A6A69E' }}>{queuedMatches} queued</span>
                <span style={{ color: '#3A3A3A' }}>·</span>
                <span className="text-xs" style={{ color: '#A6A69E' }}>{session.player_count || 0} players</span>
                {reconnecting ? (
                  <span data-testid="op-reconnecting" className="text-[10px] ml-1 px-2 py-0.5 rounded-full"
                    style={{ color: '#F59E0B', background: '#F59E0B14', border: '1px solid #F59E0B30' }}>Reconnecting…</span>
                ) : agoSec != null && (
                  <span className="text-[10px] ml-1" style={{ color: '#5A5A5A' }}>updated {agoSec}s ago</span>
                )}
              </div>
            )}
            {(session.contact_email || session.contact_phone) && (
              <p data-testid="op-public-contact" className="text-xs mt-3" style={{ color: '#525252' }}>
                Questions? Contact {session.contact_name ? <strong style={{ color: '#737373' }}>{session.contact_name}</strong> : 'the organizer'}
                {session.contact_email && <> · <a href={`mailto:${session.contact_email}`} style={{ color: RED }}>{session.contact_email}</a></>}
                {session.contact_phone && <> · {session.contact_phone}</>}
              </p>
            )}
          </div>
          {(isLive || canRegister) && (
            <div className="flex flex-col items-stretch sm:items-end gap-2 shrink-0">
              {isLive && <FindMyPass slug={slug} sid={sid} />}
              {canRegister && (
                <button data-testid="op-public-register-btn" onClick={onRegister}
                  className="px-5 py-3 rounded-full text-sm font-black uppercase tracking-wider"
                  style={isLive
                    ? { background: 'rgba(255,26,62,0.12)', color: '#fff', border: '1px solid rgba(255,26,62,0.45)', cursor: 'pointer', letterSpacing: '0.08em', fontFamily: 'Oswald, sans-serif' }
                    : { background: 'linear-gradient(135deg, #FF1A3E, #FF6B35)', color: '#fff', border: 'none', cursor: 'pointer', letterSpacing: '0.08em', boxShadow: '0 0 24px rgba(255,26,62,0.45)', fontFamily: 'Oswald, sans-serif' }}>
                  {session.status === 'active' ? 'Join Session' : 'Register Now'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── "Who's In" roster grid — grouped by division, avatar cards ────────────
function RosterSection({ session, canRegister, onRegister }) {
  const roster = session.players_public || [];
  const divs = session.divisions || [];
  if (roster.length === 0) {
    return (
      <div className="rounded-2xl p-10 text-center mb-5" style={{ background: '#0F0F0F', border: '1px dashed #2A2A2A' }}>
        <Zap className="h-8 w-8 mx-auto mb-3" style={{ color: '#2A2A2A' }} />
        <p className="text-sm" style={{ color: '#A3A3A3' }}>
          {canRegister ? 'Registration is open — be the first to sign up!' : 'No players registered yet. Check back soon.'}
        </p>
      </div>
    );
  }
  const groups = divs.length > 0
    ? divs.map(d => ({ id: d.id, name: divs.length > 1 ? d.name : null, cap: d.hard_cap, players: roster.filter(p => p.division_id === d.id) })).filter(g => g.players.length > 0)
    : [{ id: null, name: null, cap: null, players: roster }];
  const orphans = roster.filter(p => !p.division_id || !divs.some(d => d.id === p.division_id));
  if (divs.length > 0 && orphans.length) groups.push({ id: 'other', name: 'Other', cap: null, players: orphans });

  return (
    <div className="space-y-7 mb-6" data-testid="op-public-roster">
      {groups.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {groups.map(g => (
            <button key={g.id || 'all'}
              className="px-3 py-1.5 rounded-full text-xs font-bold"
              style={{ background: '#1A0A0C', color: RED, border: `1px solid ${RED}40`, cursor: 'pointer' }}
              onClick={() => document.getElementById(`op-div-${g.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
              {g.name} <span style={{ opacity: 0.7 }}>({g.players.length})</span>
            </button>
          ))}
        </div>
      )}
      {groups.map((g, gi) => {
        let offset = 0;
        for (let i = 0; i < gi; i++) offset += groups[i].players.length;
        return (
          <div key={g.id || 'all'} id={g.id ? `op-div-${g.id}` : undefined}>
            <div className="flex items-center justify-between mb-3">
              {g.name ? (
                <div className="flex items-center gap-2">
                  <div className="w-1 h-5 rounded-full" style={{ background: RED }} />
                  <h3 className="text-sm font-black uppercase tracking-wider" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff', letterSpacing: '0.08em' }}>{g.name}</h3>
                </div>
              ) : (
                <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#525252', letterSpacing: '0.12em' }}>
                  {canRegister ? "Who's In" : `${roster.length} Player${roster.length !== 1 ? 's' : ''}`}
                </p>
              )}
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold" style={{ color: '#525252' }}>
                  {g.players.length}{g.cap ? ` / ${g.cap}` : ''} player{g.players.length !== 1 ? 's' : ''}
                </span>
                {g.cap && g.players.filter(p => !p.waitlisted).length >= g.cap && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase" style={{ background: '#1A0A0C', color: RED, border: `1px solid ${RED}30` }}>FULL</span>
                )}
              </div>
            </div>
            <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))' }}>
              {g.players.map((p, idx) => (
                <div key={p.id} data-testid={`op-player-card-${p.id}`}
                  className="rounded-xl p-3 flex gap-3 items-center"
                  style={{ background: '#0F0F0F', border: '1px solid #1E1E1E' }}>
                  <div className="shrink-0" style={{ width: 42, height: 42 }}>
                    {p.avatar_url ? (
                      <img src={p.avatar_url} alt={p.name}
                        style={{ width: 42, height: 42, borderRadius: 21, objectFit: 'cover', border: '1px solid #2A2A2A' }} />
                    ) : (
                      <div className="flex items-center justify-center font-black text-sm"
                        style={{ width: 42, height: 42, borderRadius: 21, background: '#1A0A0C', color: RED, border: `1px solid ${RED}30`, fontFamily: 'Oswald, sans-serif' }}>
                        {offset + idx + 1}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-sm leading-tight truncate" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff', letterSpacing: '0.03em' }}>
                      {(p.name || '').toUpperCase()}
                    </p>
                    {p.org_label && <p className="text-xs truncate" style={{ color: '#737373' }}>{p.org_label}</p>}
                    {p.waitlisted && (
                      <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wide" style={{ background: '#8B5CF622', color: '#8B5CF6' }}>Waitlist</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      {canRegister && (
        <p className="text-xs text-center" style={{ color: '#525252' }}>
          Spots are filling up — <button data-testid="op-roster-register-link" onClick={onRegister} style={{ color: RED, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 700 }}>register now</button>
        </p>
      )}
    </div>
  );
}

// ── Post-event Podium (visible when session ended) ────────────────────────
function EventPodium({ leaderboard, session, facilityName, divisionName, extras }) {
  if (!leaderboard || leaderboard.length === 0) return null;
  // Group tied players by rank (1..3 only)
  const byRank = { 1: [], 2: [], 3: [] };
  leaderboard.forEach(p => { if (byRank[p.rank]) byRank[p.rank].push(p); });
  if (byRank[1].length === 0) return null;

  // Champions-card payload — same shape the events-page strip shares
  const fair = isFairRule(extras);
  const fmtStats = (p) => fair ? fairRecord(p) : `${p.wins} won · ${p.losses} lost · ${p.win_rate}%`
    + (typeof p.point_diff === 'number' ? ` · ${p.point_diff > 0 ? '+' : ''}${p.point_diff}` : '');
  const entry = (list) => list.length === 0 ? null : ({
    team_name: list.length === 1 ? list[0].name : list.map(p => p.name).join(' · '),
    players: list.map(p => p.name),
    avatars: list.map(p => p.avatar_url || null),
    stats: list.length === 1 ? fmtStats(list[0]) : null,
    player_stats: list.map(fmtStats),
  });
  const cardMeta = () => ({
    eventName: session?.name || 'Open Play',
    facilityName: facilityName || '',
    date: session?.date,
    division: divisionName || null,
    podium: { first: entry(byRank[1]), second: entry(byRank[2]), third: entry(byRank[3]) },
    awards: cardAwards(extras?.awards),
  });


  const STEPS = [
    { rank: 2, label: 'Runner-up', tier: '#CBD5E1', h: 118, order: 1 },
    { rank: 1, label: 'Champion', tier: '#FFD54A', h: 158, order: 2 },
    { rank: 3, label: '3rd place', tier: '#E0A86B', h: 92, order: 3 },
  ].filter(s => byRank[s.rank].length > 0);

  return (
    <div className="rounded-2xl overflow-hidden mb-5" data-testid="op-event-podium"
      style={{ background: 'linear-gradient(180deg, #1a1408 0%, #0d0d0d 60%, #0a0a0a 100%)',
        border: '1px solid rgba(255,213,74,0.25)' }}>
      <div className="flex items-center justify-between gap-2 px-5 py-3"
        style={{ background: 'rgba(255,213,74,0.06)', borderBottom: '1px solid rgba(255,213,74,0.18)' }}>
        <span className="flex items-center gap-2">
          <Trophy className="h-4 w-4" style={{ color: '#FFD54A' }} />
          <span className="text-sm font-black uppercase tracking-widest"
            style={{ color: '#FFD54A', fontFamily: 'Oswald, sans-serif', letterSpacing: '0.15em' }}>
            Champions
          </span>
        </span>
        {/* Share / save the champions card — canvas card mirrors this podium,
            ties included (every player, photo chips and all) */}
        <span className="flex gap-2">
          <button data-testid="op-podium-share-public" onClick={() => shareChampionsCard(cardMeta())}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase"
            style={{ background: 'rgba(255,213,74,0.12)', border: '1px solid rgba(255,213,74,0.3)', color: '#FFD54A' }}>
            <Share2 className="h-3 w-3" /> Share
          </button>
          <button data-testid="op-podium-download-public" onClick={() => downloadChampionsCard(cardMeta())}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase"
            style={{ background: 'transparent', border: '1px solid #333', color: '#A3A3A3' }}>
            <Download className="h-3 w-3" /> Save card
          </button>
        </span>
      </div>
      <div className="px-4 sm:px-6 pt-8 pb-6">
        <div className="flex items-end justify-center gap-3 sm:gap-6 flex-wrap sm:flex-nowrap">
          {STEPS.map(s => {
            const players = byRank[s.rank];
            const wide = players.length > 1;
            return (
              <div key={s.rank} className={`flex flex-col items-center ${wide ? 'flex-[2]' : 'flex-1'} min-w-[130px] ${wide ? 'max-w-[520px]' : 'max-w-[240px]'}`}>
                {/* Player card(s) — side-by-side when tied. EVERY tied player is
                    on the podium (fairness): first 3 get full cards, the rest
                    compact photo chips below — nobody is hidden. */}
                <div className="mb-3 flex justify-center gap-3 sm:gap-4 flex-wrap">
                  {players.slice(0, 3).map(p => (
                    <div key={p.id} className="flex flex-col items-center text-center">
                      {p.avatar_url ? (
                        <img src={p.avatar_url} alt={p.name}
                          className="rounded-full object-cover mb-1"
                          style={{ width: s.rank === 1 ? 56 : 44, height: s.rank === 1 ? 56 : 44,
                            border: `2px solid ${s.tier}`, boxShadow: `0 0 20px ${s.tier}40` }} />
                      ) : (
                        <div className="rounded-full flex items-center justify-center font-black mb-1"
                          style={{
                            width: s.rank === 1 ? 56 : 44, height: s.rank === 1 ? 56 : 44,
                            background: `${s.tier}22`, color: s.tier, border: `2px solid ${s.tier}`,
                            fontFamily: 'Oswald, sans-serif', fontSize: s.rank === 1 ? 22 : 18,
                            boxShadow: `0 0 20px ${s.tier}40`,
                          }}>
                          {p.name?.[0]?.toUpperCase()}
                        </div>
                      )}
                      <p className="font-black leading-tight"
                        style={{ fontFamily: 'Oswald, sans-serif', color: '#fff',
                          fontSize: s.rank === 1 ? 15 : 13, letterSpacing: '0.02em',
                          maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {(p.name || '').toUpperCase()}
                      </p>
                      <p className="text-[10px]" style={{ color: s.tier, fontWeight: 700 }}>
                        {fmtStats(p)}
                      </p>
                    </div>
                  ))}
                </div>
                {/* Tied players 4+ — compact chips, same rank, same podium */}
                {players.length > 3 && (
                  <div className="mb-3 flex justify-center gap-x-3 gap-y-1.5 flex-wrap px-2">
                    {players.slice(3).map(p => (
                      <span key={p.id} className="inline-flex items-center gap-1.5 min-w-0">
                        {p.avatar_url ? (
                          <img src={p.avatar_url} alt={p.name}
                            className="rounded-full object-cover shrink-0"
                            style={{ width: 18, height: 18, border: `1px solid ${s.tier}88` }} />
                        ) : (
                          <span className="rounded-full shrink-0 inline-flex items-center justify-center font-black"
                            style={{ width: 18, height: 18, background: `${s.tier}22`, color: s.tier,
                              border: `1px solid ${s.tier}66`, fontSize: 8, fontFamily: 'Oswald, sans-serif' }}>
                            {p.name?.[0]?.toUpperCase()}
                          </span>
                        )}
                        <span className="text-[11px] font-semibold truncate" style={{ color: '#fff', maxWidth: 110 }}
                          title={`${p.name} — ${p.wins} won · ${p.losses} lost`}>
                          {p.name}
                        </span>
                      </span>
                    ))}
                  </div>
                )}
                {/* Podium step */}
                <div className="w-full flex flex-col items-center justify-center gap-1.5 rounded-t-lg"
                  style={{ height: s.h,
                    background: `linear-gradient(180deg, ${s.tier}22 0%, ${s.tier}0a 100%)`,
                    border: `1px solid ${s.tier}55`, borderBottom: 'none' }}>
                  <MedalBadge rank={s.rank} size={s.rank === 1 ? 44 : 34} />
                  <span className="text-[10px] uppercase font-black"
                    style={{ color: s.tier, fontFamily: 'Oswald, sans-serif', letterSpacing: '0.14em' }}>
                    {s.label}
                  </span>
                  {players.length > 1 && (
                    <span className="text-[9px] uppercase font-bold tracking-wider"
                      style={{ color: s.tier, opacity: 0.75, letterSpacing: '0.12em' }}>
                      {players.length}-way tie
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {fair && <div className="mt-5 rounded-xl px-3 py-2.5" style={{ background: '#0B0B0B', border: '1px solid rgba(255,213,74,0.18)' }}>
          <HowRanked minGames={extras.min_games} rule={extras.podium_rule} />
        </div>}
        {extras?.awards?.length > 0 && <div className="mt-5"><OpenPlayAwards awards={extras.awards} /></div>}
      </div>
    </div>
  );
}

// ── Post-event summary strip (title, coordinator, courts, stats) ──────────
function EventSummary({ session, live }) {
  const fac = session.facility || {};
  const courtNames = (session.courts_info || []).map(c => c.name).filter(Boolean);
  const stats = [
    { label: 'Matches Played', value: live?.completed_matches || 0 },
    { label: 'Players', value: session.player_count || 0 },
    { label: 'Avg Match', value: live?.avg_match_duration ? `${live.avg_match_duration}m` : '—' },
    { label: 'Courts', value: courtNames.length || (session.courts_used || []).length || '—' },
  ];
  return (
    <div className="rounded-2xl p-5 mb-5" data-testid="op-event-summary"
      style={{ background: '#101010', border: '1px solid #262626' }}>
      <div className="flex flex-wrap gap-x-6 gap-y-2 mb-4">
        {session.date && (
          <span className="text-xs inline-flex items-center gap-1.5" style={{ color: '#A3A3A3' }}>
            <CalendarDays className="h-3.5 w-3.5" style={{ color: '#525252' }} />
            {fmtDate(session.date)}
          </span>
        )}
        {session.time_start && (
          <span className="text-xs inline-flex items-center gap-1.5" style={{ color: '#A3A3A3' }}>
            <Clock className="h-3.5 w-3.5" style={{ color: '#525252' }} />
            {fmt12(session.time_start)}{session.time_end ? ` – ${fmt12(session.time_end)}` : ''}
          </span>
        )}
        {courtNames.length > 0 && (
          <span className="text-xs inline-flex items-center gap-1.5" style={{ color: '#A3A3A3' }}>
            <Radio className="h-3.5 w-3.5" style={{ color: '#525252' }} />
            {courtNames.join(' · ')}
          </span>
        )}
        {session.contact_name && (
          <span className="text-xs inline-flex items-center gap-1.5" style={{ color: '#A3A3A3' }}>
            <Users className="h-3.5 w-3.5" style={{ color: '#525252' }} />
            Coordinator: <strong style={{ color: '#fff' }}>{session.contact_name}</strong>
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {stats.map(s => (
          <div key={s.label} className="rounded-xl px-3 py-2.5 text-center"
            style={{ background: '#0A0A0A', border: '1px solid #1E1E1E' }}>
            <p className="text-lg sm:text-xl font-black tabular-nums"
              style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>{s.value}</p>
            <p className="text-[10px] uppercase tracking-widest mt-0.5"
              style={{ color: '#525252', letterSpacing: '0.12em' }}>{s.label}</p>
          </div>
        ))}
      </div>
      {fac.name && (
        <p className="text-[10px] uppercase tracking-widest mt-4 text-center"
          style={{ color: '#333', letterSpacing: '0.15em' }}>
          Hosted at <span style={{ color: '#737373', fontWeight: 700 }}>{fac.name}</span>
        </p>
      )}
    </div>
  );
}

// ── Live sections (courts / queue / leaderboard) — active & ended ─────────
function LiveSections({ live, showCourts, session }) {
  const [activeDivIdx, setActiveDivIdx] = useState(0);
  const divisions = live?.divisions || [];
  const div = divisions[activeDivIdx] || divisions[0];
  if (!div) return null;

  return (
    <div className="mb-6">
      {/* Division tabs */}
      {divisions.length > 1 && (
        <div className="flex flex-wrap gap-1 mb-5">
          {divisions.map((d, i) => (
            <button key={d.id} onClick={() => setActiveDivIdx(i)}
              className="px-4 py-1.5 rounded-full text-xs font-bold uppercase"
              style={{
                background: i === activeDivIdx ? '#E6394622' : 'transparent',
                color: i === activeDivIdx ? RED : '#737373',
                border: `1px solid ${i === activeDivIdx ? RED : '#333'}`,
              }}>
              {d.name} · {d.active_count} active
            </button>
          ))}
        </div>
      )}

      {/* Post-event: champions podium + event summary (ended only) */}
      {!showCourts && (
        <>
          <EventSummary session={session} live={live} />
          <EventPodium leaderboard={div.leaderboard || []} session={session} extras={div}
            facilityName={session?.facility?.name}
            divisionName={divisions.length > 1 ? div.name : null} />
        </>
      )}

      {/* Layout: two-column while LIVE, single column (full-width board) when ENDED */}
      <div className={`grid grid-cols-1 gap-5 ${showCourts ? 'lg:grid-cols-3' : ''}`}>
        {/* ── LEFT COLUMN (only rendered while live) ── */}
        {showCourts && (
        <div className="lg:col-span-2 space-y-5">

          {/* Now Playing — each court its own card; a lone court gets the full
              width so the broadcast card never squeezes names */}
          {showCourts && (
            <div className={`grid grid-cols-1 gap-4 ${
              (div.courts || []).filter(c => (c.slots || []).length > 0).length > 1 ? 'sm:grid-cols-2' : ''}`}>
              {(div.courts || []).map(court => {
            const courtSlots = (court.slots || []).filter((_, si) => si < 4);
            if (courtSlots.length === 0 && court.paused) {
              return (
                <div key={court.court_id} data-testid={`live-court-closed-${court.court_id}`}
                  className="flex items-center gap-2 px-4 py-3 rounded-2xl"
                  style={{ background: '#141414', border: '1px solid #262626' }}>
                  <span className="text-sm font-black uppercase tracking-wide"
                    style={{ fontFamily: 'Oswald, sans-serif', color: '#737373' }}>
                    {court.court_name}
                  </span>
                  <span className="ml-auto text-[10px] font-extrabold uppercase tracking-wider" style={{ color: '#737373' }}>
                    Closed for now
                  </span>
                </div>
              );
            }
            if (courtSlots.length === 0) return null;
            const activeSlot = courtSlots.find(s => s.status === 'active');
            const onCourtSince = activeSlot?.on_court_at || activeSlot?.started_at;
            const elapsedMin = onCourtSince
              ? Math.max(0, Math.round((Date.now() - new Date(onCourtSince).getTime()) / 60000))
              : null;
            return (
              <div key={court.court_id} className="rounded-2xl overflow-hidden"
                style={{ background: '#141414', border: '1px solid #262626' }}>
                {/* Court header — name + LIVE + elapsed (checklist 1.4) */}
                <div className="flex items-center gap-2 px-4 py-3"
                  style={{ background: '#1C1C1C', borderBottom: '1px solid #262626' }}>
                  <span className="text-sm font-black uppercase tracking-wide"
                    style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>
                    {court.court_name}
                  </span>
                  {activeSlot && court.pause_after_game && (
                    <span data-testid={`live-court-last-game-${court.court_id}`}
                      className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded"
                      style={{ background: '#F59E0B14', color: '#F59E0B', border: '1px solid #F59E0B40' }}>
                      Last game for now
                    </span>
                  )}
                  {activeSlot && (
                    <span className="inline-flex items-center gap-1.5 ml-auto text-[10px] font-extrabold uppercase tracking-wider"
                      style={{ color: '#F59E0B' }}>
                      <span className="h-1.5 w-1.5 rounded-full animate-pulse"
                        style={{ background: '#F59E0B', boxShadow: '0 0 8px rgba(245,158,11,0.8)' }} />
                      LIVE{elapsedMin != null ? ` · ${elapsedMin} min` : ''}
                    </span>
                  )}
                </div>
                {/* Matches — shared MatchupCard, live state carries the score */}
                <div className="p-3 space-y-2">
                  {courtSlots.map((slot, si) => {
                    const isActive = slot.status === 'active';
                    return (
                      <div key={slot.id} className="rounded-xl overflow-hidden"
                        style={{
                          background: isActive ? 'rgba(230,57,70,0.07)' : '#0F0F0F',
                          border: `1px solid ${isActive ? '#E6394630' : '#1E1E1E'}`,
                        }}>
                        {!isActive && (
                          <div className="px-3 pt-2 text-[9px] font-bold uppercase tracking-wider" style={{ color: '#555' }}>
                            {si === 1 ? 'On deck' : 'Next up'} on this court
                          </div>
                        )}
                        <MatchupCard
                          variant={isActive ? 'live' : 'queued'}
                          forceStacked
                          teamA={slot.team_a_players || []} teamB={slot.team_b_players || []}
                          namesA={slot.team_a_names} namesB={slot.team_b_names}
                          scoreA={slot.score_a} scoreB={slot.score_b}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
            </div>
          )}

          {/* Called matches waiting for a court (TBA) — they take the next free
              courts before the queue; shown with their live score (R1-108) */}
          {showCourts && (div.tba || []).length > 0 && (
            <div className="rounded-2xl overflow-hidden" data-testid="tba-matches"
              style={{ background: '#141414', border: '1px solid #F59E0B40' }}>
              <div className="flex items-center justify-between gap-2 px-4 py-3"
                style={{ background: '#1C1C1C', borderBottom: '1px solid #1A1A1A' }}>
                <span className="text-sm font-black uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif', color: '#F59E0B' }}>
                  Called — next free court
                </span>
                <span className="text-[10px] text-right" style={{ color: '#555' }}>goes on before the queue</span>
              </div>
              <div className="p-3 space-y-2">
                {(div.tba || []).map(slot => (
                  <div key={slot.id} className="rounded-xl overflow-hidden" style={{ background: '#0F0F0F', border: '1px solid #1E1E1E' }}>
                    <MatchupCard variant={(slot.score_a || slot.score_b) ? 'live' : 'queued'} forceStacked
                      teamA={slot.team_a_players || []} teamB={slot.team_b_players || []}
                      namesA={slot.team_a_names} namesB={slot.team_b_names}
                      scoreA={slot.score_a} scoreB={slot.score_b}
                      scoreNote={slot.eta_minutes ? `court in ${fmtWait(slot.eta_minutes)}` : null}
                      etaMinutes={slot.eta_minutes} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Match queue — departures board (checklist 1.5): one strict FIFO list,
              quiet set dividers, per-match "Court in ~X min" from the backend */}
          {showCourts && ((div.pool || []).length > 0 || (div.open_games || []).length > 0 ? (
            <div className="rounded-2xl overflow-hidden" data-testid="match-queue"
              style={{ background: '#141414', border: '1px solid #262626' }}>
              <div className="flex items-center justify-between gap-2 px-4 py-3"
                style={{ background: '#1C1C1C', borderBottom: '1px solid #1A1A1A' }}>
                <span className="text-sm font-black uppercase tracking-wide inline-flex items-center gap-2"
                  style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>
                  Match queue
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase"
                    style={{ background: '#F59E0B14', color: '#F59E0B', border: '1px solid #F59E0B30' }}>
                    Shared queue
                  </span>
                </span>
                <span className="text-[10px] text-right" style={{ color: '#555' }}>
                  paired · waiting for a free court
                </span>
              </div>
              <div>
                {(() => {
                  // The line as courts take it: a TBA game sits at the end of its set.
                  const pool = div.pool || [];
                  const opens = div.open_games || [];
                  const items = [];
                  pool.forEach((sl, si) => {
                    opens.filter(g => g.pool_index === si).forEach(g => items.push({ open: g, set_number: g.set_number }));
                    items.push({ slot: sl, set_number: sl.set_number });
                  });
                  opens.filter(g => g.pool_index >= pool.length).forEach(g => items.push({ open: g, set_number: g.set_number }));
                  return items;
                })().map((item, i, items) => {
                  const prev = i > 0 ? items[i - 1] : null;
                  const newSet = !prev || prev.set_number !== item.set_number;
                  if (item.open) {
                    const g = item.open;
                    const seats = [...(g.fixed || []).map(p => p.name), ...Array.from({ length: g.open_seats }, () => null)];
                    const half = Math.ceil(seats.length / 2);
                    const side = (list, color) => (
                      <span className="inline-flex flex-wrap items-center gap-1.5" style={{ color }}>
                        {list.map((nm, k) => (
                          <Fragment key={k}>
                            {k > 0 && <span style={{ color: '#555' }}>&amp;</span>}
                            {nm ? <span className="font-bold">{nm}</span> : (
                              <span className="text-[10.5px] font-extrabold px-1.5 py-px rounded-md"
                                style={{ border: '1.5px dashed #F59E0B88', color: '#FCD34D', background: '#F59E0B0A' }}>TBA</span>
                            )}
                          </Fragment>
                        ))}
                      </span>
                    );
                    return (
                      <div key={g.id} data-testid={`live-open-${g.id}`}>
                        {newSet && (
                          <div className="flex items-center gap-3 px-4 pt-2.5 pb-1">
                            <div style={{ flex: 1, height: 1, background: '#1A1A1A' }} />
                            <span className="text-[9.5px] font-bold uppercase shrink-0"
                              style={{ color: '#525252', letterSpacing: '0.14em' }}>Set {g.set_number} — Rotation</span>
                            <div style={{ flex: 1, height: 1, background: '#1A1A1A' }} />
                          </div>
                        )}
                        <div className="grid items-center py-2 relative"
                          style={{ gridTemplateColumns: '2.1rem minmax(0,1fr)', borderTop: !newSet ? '1px solid #1A1A1A' : 'none',
                            background: 'linear-gradient(90deg, #F59E0B10, transparent 70%)' }}>
                          <span className="text-base font-black tabular-nums text-right pl-2"
                            style={{ color: i === 0 ? '#F59E0B' : '#444', fontFamily: 'Oswald, sans-serif' }}>#{i + 1}</span>
                          <div className="min-w-0 px-3 text-[13px]">
                            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                              <span className="text-[9.5px] font-extrabold px-1.5 py-0.5 rounded"
                                style={{ background: '#F59E0B', color: '#111', letterSpacing: '0.06em' }}>TBA GAME</span>
                              {side(seats.slice(0, half), '#FF8A93')}
                              <span style={{ color: '#555' }}>vs</span>
                              {side(seats.slice(half), '#8DBBFF')}
                            </div>
                            <p className="text-[11px] mt-1" style={{ color: '#8A8A8A' }}>
                              Players picked about one game before{g.eta_minutes ? ` · about ${g.eta_minutes} min` : ''}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  const slot = item.slot;
                  return (
                    <div key={slot.id}>
                      {newSet && (
                        <div className="flex items-center gap-3 px-4 pt-2.5 pb-1">
                          <div style={{ flex: 1, height: 1, background: '#1A1A1A' }} />
                          <span className="text-[9.5px] font-bold uppercase shrink-0"
                            style={{ color: '#525252', letterSpacing: '0.14em' }}>
                            Set {slot.set_number} — {slot.set_type === 'skill_based' ? 'Ranked' : 'Rotation'}
                          </span>
                          <div style={{ flex: 1, height: 1, background: '#1A1A1A' }} />
                        </div>
                      )}
                      <div className="grid items-center"
                        style={{ gridTemplateColumns: '2.1rem minmax(0,1fr)',
                          borderTop: !newSet ? '1px solid #1A1A1A' : 'none' }}>
                        <span className="text-base font-black tabular-nums text-right pl-2"
                          style={{ color: i === 0 ? '#F59E0B' : '#444', fontFamily: 'Oswald, sans-serif' }}>
                          #{i + 1}
                        </span>
                        <MatchupCard variant="queued" nextUp={i === 0}
                          teamA={slot.team_a_players || []} teamB={slot.team_b_players || []}
                          namesA={slot.team_a_names} namesB={slot.team_b_names}
                          etaMinutes={slot.eta_minutes} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="rounded-2xl p-6 text-center" style={{ background: '#141414', border: '1px solid #262626' }}>
              <p className="text-sm" style={{ color: '#444' }}>No matches in the queue right now</p>
              <p className="text-xs mt-1" style={{ color: '#333' }}>The next set will appear here as soon as it's generated</p>
            </div>
          ))}

          {/* On the bench (checklist 1.6) — longest-benched first, honest
              pool-clear ETA: "joins Set N · est. ~X min" */}
          {showCourts && (div.queue || []).length > 0 && (
            <div className="rounded-2xl overflow-hidden" data-testid="bench-queue"
              style={{ background: '#141414', border: '1px solid #262626' }}>
              <div className="flex items-center justify-between gap-2 px-4 py-3"
                style={{ background: '#1C1C1C', borderBottom: '1px solid #1A1A1A' }}>
                <span className="text-sm font-black uppercase tracking-wide"
                  style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>
                  On the bench
                </span>
                <span className="text-[10px] text-right" style={{ color: '#555' }}>
                  not paired yet — joins when the next set is generated
                </span>
              </div>
              <div className="divide-y" style={{ borderColor: '#1A1A1A' }}>
                {(div.queue || []).slice(0, 10).map((p, i) => (
                  <div key={p.id} className="flex items-center gap-3 px-4 py-2.5"
                    style={{ background: i === 0 ? 'rgba(245,158,11,0.045)' : 'transparent' }}>
                    <span className="text-sm font-black tabular-nums w-7 text-right shrink-0"
                      style={{ color: i === 0 ? '#F59E0B' : '#444', fontFamily: 'Oswald, sans-serif' }}>
                      #{i + 1}
                    </span>
                    <span className="text-sm font-semibold flex-1 min-w-0 truncate" style={{ color: '#fff' }}>
                      {p.name}
                      {i === 0 && (
                        <span className="ml-2 text-[9px] font-bold uppercase tracking-wider" style={{ color: '#F59E0B' }}>
                          longest on the bench
                        </span>
                      )}
                    </span>
                    {p.matches_played > 0 && (
                      <span className="text-[10px] shrink-0" style={{ color: '#525252' }}>
                        {p.matches_played} played · {p.wins} won
                      </span>
                    )}
                    {p.eta_minutes != null && (
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full shrink-0"
                        style={{ background: '#F59E0B14', color: '#F59E0B', border: '1px solid #F59E0B30' }}>
                        joins Set {p.joins_set_number} · est. {fmtWait(p.eta_minutes) || 'soon'}
                      </span>
                    )}
                  </div>
                ))}
                {(div.queue || []).length > 10 && (
                  <div className="px-4 py-2 text-xs text-center" style={{ color: '#525252' }}>
                    + {(div.queue || []).length - 10} more waiting
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Flagged / paused players */}
          {div.flagged_players?.length > 0 && showCourts && (
            <div className="flex flex-wrap gap-2 px-1">
              {div.flagged_players.map(p => (
                <span key={p.id} className="text-xs px-2.5 py-1 rounded-full"
                  style={{
                    background: p.status === 'paused' ? '#F59E0B12' : '#EF444412',
                    color: p.status === 'paused' ? '#F59E0B' : '#EF4444',
                    border: `1px solid ${p.status === 'paused' ? '#F59E0B25' : '#EF444425'}`,
                  }}>
                  {p.name} — {p.status}{p.no_show_streak > 0 ? ` (${p.no_show_streak}x no-show)` : ''}
                </span>
              ))}
            </div>
          )}
        </div>
        )}

        {/* ── LEADERBOARD (sticky right column while live, full-width when ended) ── */}
        <div className={showCourts ? 'lg:col-span-1' : ''}>
          <div className={`rounded-2xl overflow-hidden ${showCourts ? 'sticky top-4' : ''}`}
            style={{ background: '#141414', border: '1px solid #262626' }}>
            <div className="px-4 py-3 flex items-center justify-between"
              style={{ background: '#1C1C1C', borderBottom: '1px solid #262626' }}>
              <h2 className="text-sm font-black uppercase tracking-wide flex items-center gap-1.5"
                style={{ fontFamily: 'Oswald, sans-serif', color: '#F59E0B' }}>
                <Trophy className="h-4 w-4" />
                {showCourts ? 'Leaderboard' : 'Final Standings'}
              </h2>
              {!showCourts && (
                <span className="text-[10px] uppercase tracking-widest hidden sm:inline"
                  style={{ color: '#555', letterSpacing: '0.14em' }}>
                  {isWinsRule(div) ? 'W–L · win rate' : isFairRule(div) ? 'W–L · share of points' : 'W · L · Win% · +/−'}
                </span>
              )}
            </div>
            {(!div.leaderboard || div.leaderboard.length === 0) ? (
              <p className="text-xs text-center py-8" style={{ color: '#555' }}>No matches completed yet</p>
            ) : (
              <div className={!showCourts
                ? 'grid grid-cols-1 md:grid-cols-2 gap-x-4 divide-y md:divide-y-0'
                : 'divide-y'} style={{ borderColor: '#1A1A1A' }}>
                {div.leaderboard.map((p) => {
                  const rank = p.rank || 0;
                  const fair = isFairRule(div);
                  const unranked = fair && p.rank == null;
                  const rankColor = rank === 1 ? '#FFD54A' : rank === 2 ? '#CBD5E1' : rank === 3 ? '#E0A86B' : '#555';
                  return (
                    <div key={p.id} data-testid={`op-leaderboard-row-${p.id}`}
                      className="flex items-center gap-3 px-4 py-2.5"
                      title={unranked ? `Not on the podium — ${unrankedWhy(p, div.min_games)}` : undefined}
                      style={{ borderBottom: !showCourts ? '1px solid #1A1A1A' : undefined, opacity: unranked ? 0.55 : 1 }}>
                      <span className="w-8 shrink-0 flex items-center">
                        <MedalBadge rank={unranked ? '–' : rank} size={22} />
                      </span>
                      {p.avatar_url ? (
                        <img src={p.avatar_url} alt={p.name}
                          className="h-8 w-8 rounded-full object-cover shrink-0"
                          style={{ border: `1px solid ${rankColor}55` }} />
                      ) : (
                        <div className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-black shrink-0"
                          style={{ background: `${rankColor}22`, color: rankColor,
                            border: `1px solid ${rankColor}55`, fontFamily: 'Oswald, sans-serif' }}>
                          {p.name?.[0]?.toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate" style={{ color: '#fff' }}>
                          {p.name}
                          {p.consistent_winner && <span className="ml-1 text-xs" title="Consistent winner" style={{ color: '#F59E0B' }}>★</span>}
                        </p>
                        <p className="text-[10px]" style={{ color: '#666' }}>
                          {fair ? <>{fairRecord(p)}{unranked && ` · ${unrankedWhy(p, div.min_games)}`}</> : <>
                          {p.matches_played} played · {p.wins} won · {p.win_rate}%
                          {typeof p.point_diff === 'number' && (
                            <span style={{ color: p.point_diff > 0 ? '#22C55E' : p.point_diff < 0 ? '#EF4444' : '#666', marginLeft: 4 }}>
                              · {p.point_diff > 0 ? '+' : ''}{p.point_diff}
                            </span>
                          )}</>}
                        </p>
                      </div>
                      <span className="text-sm font-black tabular-nums shrink-0"
                        style={{ color: p.wins > 0 ? '#22C55E' : '#555', fontFamily: 'Oswald, sans-serif' }}>
                        {p.wins}W
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SportsOpenPlayLivePage() {
  const { slug, sid } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reconnecting, setReconnecting] = useState(false);

  // A failed poll keeps the last board and shows "Reconnecting…"; the full
  // error screen is only for a real 404 before anything has loaded (R1-111).
  const fetchDetail = useCallback(async () => {
    try {
      const r = await api.get(`/sports/public/${slug}/open-play/${sid}`);
      setSession(r.data);
      setError(null);
      setReconnecting(false);
      return r.data;
    } catch (e) {
      if (e.response?.status === 404) {
        setSession(prev => {
          if (!prev) setError(e.response?.data?.detail || 'Session not found');
          return prev;
        });
      } else {
        setReconnecting(true);
      }
      return null;
    } finally { setLoading(false); }
  }, [slug, sid]);

  const [lastUpdated, setLastUpdated] = useState(null);
  const fetchLive = useCallback(async () => {
    try {
      const r = await api.get(`/sports/public/${slug}/open-play/${sid}/live`);
      setLive(r.data);
      setLastUpdated(Date.now());
      setReconnecting(false);
    } catch { setReconnecting(true); }
  }, [slug, sid]);

  useEffect(() => {
    fetchDetail().then(s => {
      if (s && ['active', 'ended'].includes(s.status)) fetchLive();
    });
  }, [fetchDetail, fetchLive]);

  // Poll: fast while LIVE; every 15 s before Start (any pre-start state) so a
  // screen left open flips to LIVE on its own; paused while the tab is hidden.
  useEffect(() => {
    if (!session) return undefined;
    const visible = () => !document.hidden;
    if (session.status === 'active') {
      fetchLive();  // the board shows now, not one interval after Start
      const iv = setInterval(() => { if (visible()) { fetchDetail(); fetchLive(); } }, 5000);
      return () => clearInterval(iv);
    }
    if (['draft', 'registration_open', 'registration_closed'].includes(session.status)) {
      const iv = setInterval(() => { if (visible()) fetchDetail(); }, 15000);
      return () => clearInterval(iv);
    }
    if (session.status === 'ended' && !live?.session?.results_finalized_at) {
      // Caught the End mid-save: read the final results now, and again until
      // End has saved every game (the page used to freeze on a stale board).
      fetchLive();
      const iv = setInterval(fetchLive, 15000);
      return () => clearInterval(iv);
    }
    return undefined;
  }, [session?.status, live?.session?.results_finalized_at, fetchDetail, fetchLive]); // eslint-disable-line react-hooks/exhaustive-deps

  // Coming back to the tab refreshes at once instead of waiting for the next tick.
  useEffect(() => {
    const onVis = () => {
      if (document.hidden) return;
      fetchDetail().then(sd => { if (sd && ['active', 'ended'].includes(sd.status)) fetchLive(); });
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [fetchDetail, fetchLive]);

  // Per-event browser tab title (parity with tournaments)
  useEffect(() => {
    if (session?.name) document.title = session.facility?.name ? `${session.name} — ${session.facility.name}` : session.name;
    return () => { document.title = 'AgriBooks | Smart Business Management System'; };
  }, [session]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0A0A' }}>
      <Zap className="h-8 w-8 animate-pulse" style={{ color: RED }} />
    </div>
  );
  if (error) return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#0A0A0A' }}>
      <div className="text-center">
        <AlertTriangle className="h-10 w-10 mx-auto mb-3" style={{ color: '#EF4444' }} />
        <p className="text-lg font-bold" style={{ color: '#fff' }}>{error}</p>
      </div>
    </div>
  );
  if (!session) return null;

  const canRegister = session.registration_is_open != null ? !!session.registration_is_open
    : ['registration_open', 'active'].includes(session.status);
  const goRegister = () => navigate(`/book/${slug}/open-play/${sid}/register`);

  return (
    <div className="min-h-screen" style={{ background: '#0A0A0A' }}>
      <div className={`${session.status === 'ended' ? 'max-w-6xl' : 'max-w-5xl'} mx-auto px-4 py-6`}>
        <button onClick={() => navigate(`/book/${slug}`)} data-testid="op-public-back"
          className="flex items-center gap-2 text-sm mb-4" style={{ color: '#737373', background: 'none', border: 'none', cursor: 'pointer' }}>
          <ArrowLeft className="h-4 w-4" />
          {session.facility?.logo_url && (
            <img src={session.facility.logo_url} alt="" style={{ width: 20, height: 20, borderRadius: 4, objectFit: 'cover' }} />
          )}
          {session.facility?.name}
        </button>

        <SessionHero session={session} canRegister={canRegister} onRegister={goRegister}
          live={live} lastUpdated={lastUpdated} slug={slug} sid={sid} reconnecting={reconnecting} />

        {['active', 'ended'].includes(session.status) && live && (
          <LiveSections live={live} showCourts={session.status === 'active'} session={session} />
        )}

        <RosterSection session={session} canRegister={canRegister} onRegister={goRegister} />
      </div>
    </div>
  );
}
